import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { supabase } from '@/lib/supabase'
import type { FactoryInfo } from '@/hooks/useFactoryInfo'

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  company: z.string().optional(),
  phone: z.string().optional(),
  subject: z.string().min(5, 'Subject must be at least 5 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
})

type ContactFormData = z.infer<typeof contactSchema>

export function ContactPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { data: contactInfo } = useQuery({
    queryKey: ['contact-info'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'contact_info')
        .single()
      if (error) return null
      return data.value as {
        company_name?: string
        address?: string
        city?: string
        country?: string
        phone?: string
        email?: string
        footer_logo?: string
        footer_logo_link?: string
      }
    },
    staleTime: 60000,
  })

  // Fetch factory/office locations
  const { data: factoryInfo = [] } = useQuery({
    queryKey: ['factory-info', 'public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('factory_info')
        .select('*')
        .order('is_primary', { ascending: false })
        .order('name')
      if (error) return []
      return data as FactoryInfo[]
    },
    staleTime: 60000,
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  })

  const onSubmit = async (data: ContactFormData) => {
    // TODO: Submit to Supabase
    console.log(data)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsSubmitted(true)
  }

  return (
    <>
      <SEO pageKey="contact" fallback={{ title: 'Contact EngiMarineX - Get a Quote for Marine Equipment' }} />
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-lg text-secondary-300 leading-relaxed">
              Have questions about our products or need a custom quote? Our team
              is here to help.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  Get in Touch
                </h2>
                <p className="text-secondary-600">
                  We'd love to hear from you. Send us a message and we'll respond
                  as soon as possible.
                </p>
              </div>

              {/* Headoffice - Contact Info from Settings */}
              {contactInfo && (contactInfo.address || contactInfo.phone || contactInfo.email) && (
                <div>
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">Headoffice</h3>
                  <div className="space-y-4">
                    {contactInfo.address && (
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-11 h-11 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          {contactInfo.company_name && (
                            <p className="font-semibold text-secondary-900">{contactInfo.company_name}</p>
                          )}
                          <p className="text-secondary-600 text-sm">
                            {contactInfo.address}
                            {(contactInfo.city || contactInfo.country) && (
                              <>
                                <br />
                                {[contactInfo.city, contactInfo.country].filter(Boolean).join(', ')}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    {contactInfo.phone && (
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-11 h-11 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-secondary-900">Phone</p>
                          <a
                            href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                            className="text-secondary-600 text-sm hover:text-primary-600"
                          >
                            {contactInfo.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {contactInfo.email && (
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-11 h-11 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-secondary-900">Email</p>
                          <a
                            href={`mailto:${contactInfo.email}`}
                            className="text-secondary-600 text-sm hover:text-primary-600"
                          >
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Factory/Office Locations */}
              {factoryInfo.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">
                    {factoryInfo.length > 1 ? 'Factories' : 'Factory'}
                  </h3>
                  <div className="space-y-4">
                    {factoryInfo.map((factory) => (
                      <div key={factory.id} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-11 h-11 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          {factory.name && (
                            <p className="font-semibold text-secondary-900">{factory.name}</p>
                          )}
                          <p className="text-secondary-600 text-sm">
                            {factory.address}
                            <br />
                            {factory.city}, {factory.country}
                          </p>
                          {factory.phone && (
                            <a
                              href={`tel:${factory.phone.replace(/\s/g, '')}`}
                              className="text-secondary-600 text-sm hover:text-primary-600 block mt-1"
                            >
                              {factory.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Factory email - show first factory's email */}
                    {factoryInfo[0]?.email && (
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-11 h-11 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <a
                            href={`mailto:${factoryInfo[0].email}`}
                            className="text-secondary-600 text-sm hover:text-primary-600"
                          >
                            {factoryInfo[0].email}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Logo */}
              {contactInfo?.footer_logo && (
                <div className="mt-8">
                  {contactInfo.footer_logo_link ? (
                    <a
                      href={contactInfo.footer_logo_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-secondary-100 rounded-lg p-3 hover:bg-secondary-200 transition-colors"
                    >
                      <img
                        src={contactInfo.footer_logo}
                        alt="Partner logo"
                        className="w-full max-h-16 object-contain"
                      />
                    </a>
                  ) : (
                    <div className="bg-secondary-100 rounded-lg p-3">
                      <img
                        src={contactInfo.footer_logo}
                        alt="Partner logo"
                        className="w-full max-h-16 object-contain"
                      />
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              {isSubmitted ? (
                <div className="card p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-secondary-600">
                    Thank you for contacting us. We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="card p-8 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        {...register('name')}
                        className="input"
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        {...register('email')}
                        className="input"
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        {...register('company')}
                        className="input"
                        placeholder="Company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        {...register('phone')}
                        className="input"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      {...register('subject')}
                      className="input"
                      placeholder="How can we help?"
                    />
                    {errors.subject && (
                      <p className="mt-1 text-sm text-red-500">{errors.subject.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      {...register('message')}
                      rows={5}
                      className="input resize-none"
                      placeholder="Your message..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full py-3"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </Container>
      </section>
    </>
  )
}
